import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Star, Minus, Plus, Heart } from 'lucide-react';
import { productsAPI } from '../api/products';
import { Product, ProductVariant, Review } from '../types';
import { useCartStore } from '../store/cartStore';
import { useAuthStore } from '../store/authStore';
import { ProductDetailSkeleton } from '../components/ui/SkeletonLoader';
import { useCurrencyStore } from '../store/currencyStore';
import MeasurementModal, { MeasurementData } from '../components/product/MeasurementModal';
import api from '../api/axios';
import toast from 'react-hot-toast';

const ProductPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { format } = useCurrencyStore();
  const [product, setProduct] = useState<Product | null>(null);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedVariant, setSelectedVariant] = useState<ProductVariant | null>(null);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [activeTab, setActiveTab] = useState<'description' | 'details' | 'reviews'>('description');
  const [showMeasurementModal, setShowMeasurementModal] = useState(false);
  const [showSizeGuide, setShowSizeGuide] = useState(false);
  const [isSubmittingMeasurements, setIsSubmittingMeasurements] = useState(false);
  const { addToCart } = useCartStore();
  const { isAuthenticated } = useAuthStore();

  useEffect(() => {
    const fetchProduct = async () => {
      if (!slug) return;
      try {
        setIsLoading(true);
        const res = await productsAPI.getOne(slug);
        if (res.success && res.data) {
          setProduct(res.data);
          if (res.data.variants?.length > 0) {
            setSelectedVariant(res.data.variants[0]);
            setSelectedSize(res.data.variants[0].size || '');
            setSelectedColor(res.data.variants[0].color || '');
          }
        }
      } catch {
        toast.error('Product not found');
      } finally {
        setIsLoading(false);
      }
    };
    fetchProduct();
  }, [slug]);

  const handleVariantSelect = (size?: string, color?: string) => {
    if (!product) return;
    const variant = product.variants.find(
      (v) =>
        (size ? v.size === size : true) &&
        (color ? v.color === color : true)
    );
    if (variant) {
      setSelectedVariant(variant);
      if (size) setSelectedSize(size);
      if (color) setSelectedColor(color);
    }
  };

  const requiresMeasurements = Boolean((product as any)?.requires_measurements);

  const handleAddToCart = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to add to bag');
      return;
    }
    if (!selectedVariant) {
      toast.error('Please select a variant');
      return;
    }
    if (selectedVariant.stock_quantity < quantity) {
      toast.error('Insufficient stock');
      return;
    }
    if (requiresMeasurements) {
      setShowMeasurementModal(true);
      return;
    }
    try {
      await addToCart(product!.id, selectedVariant.id, quantity);
      toast.success('Added to bag');
    } catch {
      toast.error('Failed to add to bag');
    }
  };

  const handleMeasurementSubmit = async (measurements: MeasurementData) => {
    if (!selectedVariant) return;
    try {
      setIsSubmittingMeasurements(true);
      await addToCart(product!.id, selectedVariant.id, quantity, measurements as Record<string, string | undefined>);
      toast.success('Added to bag with your measurements');
      setShowMeasurementModal(false);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to add to bag');
    } finally {
      setIsSubmittingMeasurements(false);
    }
  };

  const handleWishlist = async () => {
    if (!isAuthenticated) {
      toast.error('Please login');
      return;
    }
    try {
      if (isWishlisted) {
        await api.delete(`/api/users/wishlist/${product?.id}`);
        setIsWishlisted(false);
      } else {
        await api.post(`/api/users/wishlist/${product?.id}`);
        setIsWishlisted(true);
      }
    } catch {
      toast.error('Failed to update wishlist');
    }
  };

  const sizes = Array.from(new Set(product?.variants?.map((v) => v.size).filter(Boolean)));
  const colors = Array.from(new Map(
    product?.variants
      ?.filter((v) => v.color)
      ?.map((v) => [v.color, v]) || []
  ).values());

  if (isLoading) return <ProductDetailSkeleton />;
  if (!product) return (
    <div className="min-h-screen flex items-center justify-center">
      <p className="text-xs tracking-widest uppercase text-nude">Product not found</p>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-6 py-12 animate-fade-in">
      {/* Breadcrumb */}
      <div className="flex items-center space-x-2 text-xs tracking-widest uppercase text-nude mb-8">
        <Link to="/" className="hover:text-primary transition-colors">Home</Link>
        <span>/</span>
        <Link to="/shop" className="hover:text-primary transition-colors">Shop</Link>
        <span>/</span>
        <span className="text-primary">{product.name}</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
        {/* Images */}
        <div className="space-y-4">
          {/* Main Image */}
          <div className="relative aspect-[3/4] bg-light overflow-hidden">
            <AnimatePresence mode="wait">
              <motion.img
                key={selectedImage}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4 }}
                src={product.images[selectedImage]?.image_url || '/placeholder.jpg'}
                alt={product.name}
                className="w-full h-full object-cover"
              />
            </AnimatePresence>

            {product.images.length > 1 && (
              <>
                <button
                  onClick={() => setSelectedImage(Math.max(0, selectedImage - 1))}
                  className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/80 p-2 hover:bg-white transition-colors"
                  disabled={selectedImage === 0}
                >
                  <ChevronLeft size={16} />
                </button>
                <button
                  onClick={() => setSelectedImage(Math.min(product.images.length - 1, selectedImage + 1))}
                  className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/80 p-2 hover:bg-white transition-colors"
                  disabled={selectedImage === product.images.length - 1}
                >
                  <ChevronRight size={16} />
                </button>
              </>
            )}
          </div>

          {/* Thumbnails */}
          {product.images.length > 1 && (
            <div className="grid grid-cols-4 gap-2">
              {product.images.map((img, index) => (
                <button
                  key={img.id}
                  onClick={() => setSelectedImage(index)}
                  className={`aspect-square overflow-hidden border-2 transition-colors ${
                    selectedImage === index ? 'border-primary' : 'border-transparent'
                  }`}
                >
                  <img
                    src={img.image_url}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Product Info */}
        <div className="space-y-8">
          {product.category_name && (
            <p className="text-xs tracking-widest uppercase text-nude">
              {product.category_name}
            </p>
          )}

          <div>
            <h1 className="font-serif text-4xl text-primary mb-4">
              {product.name}
            </h1>
         <div className="flex items-center space-x-4">
  <span className="text-2xl text-primary">
    {format(Number(product.price))}
  </span>
  {product.compare_at_price && (
    <span className="text-sm text-nude line-through">
      {format(Number(product.compare_at_price))}
    </span>
  )}
</div>
            {requiresMeasurements && (
              <p className="text-xs tracking-widest uppercase text-amber-600 mt-3">
                Made to Measure — Measurements Required
              </p>
            )}
          </div>

          {/* Rating */}
          {product.avg_rating && (
            <div className="flex items-center space-x-2">
              <div className="flex space-x-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    size={12}
                    className={
                      star <= Math.round(product.avg_rating!)
                        ? 'fill-primary text-primary'
                        : 'text-nude'
                    }
                  />
                ))}
              </div>
              <span className="text-xs text-nude tracking-wide">
                ({product.review_count} reviews)
              </span>
            </div>
          )}

          {/* Size Selection */}
          {sizes.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs tracking-widest uppercase">Size</h3>
                <button
                  onClick={() => setShowSizeGuide(true)}
                  className="text-xs text-nude tracking-wide underline hover:text-primary transition-colors"
                >
                  Size Guide
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {sizes.map((size) => (
                  <button
                    key={size}
                    onClick={() => handleVariantSelect(size!, selectedColor)}
                    className={`px-4 py-2 text-xs tracking-widest uppercase border transition-colors ${
                      selectedSize === size
                        ? 'border-primary bg-primary text-white'
                        : 'border-light text-primary hover:border-primary'
                    }`}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Color Selection */}
          {colors.length > 0 && (
            <div>
              <h3 className="text-xs tracking-widest uppercase mb-3">
                Color: <span className="text-nude normal-case">{selectedColor}</span>
              </h3>
              <div className="flex space-x-3">
                {colors.map((variant) => (
                  <button
                    key={variant.id}
                    onClick={() => handleVariantSelect(selectedSize, variant.color!)}
                    className={`w-8 h-8 rounded-full border-2 transition-all ${
                      selectedColor === variant.color
                        ? 'border-primary scale-110'
                        : 'border-transparent'
                    }`}
                    style={{ backgroundColor: variant.color_hex || '#ccc' }}
                    title={variant.color || ''}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Stock Status */}
          {selectedVariant && (
            <p className={`text-xs tracking-widest uppercase ${
              selectedVariant.stock_quantity > 5
                ? 'text-green-600'
                : selectedVariant.stock_quantity > 0
                ? 'text-amber-600'
                : 'text-red-500'
            }`}>
              {selectedVariant.stock_quantity > 5
                ? 'In Stock'
                : selectedVariant.stock_quantity > 0
                ? `Only ${selectedVariant.stock_quantity} left`
                : 'Out of Stock'}
            </p>
          )}

          {/* Quantity */}
          <div>
            <h3 className="text-xs tracking-widest uppercase mb-3">Quantity</h3>
            <div className="flex items-center border border-light w-fit">
              <button
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                className="p-3 hover:bg-light transition-colors"
              >
                <Minus size={14} />
              </button>
              <span className="px-6 text-sm">{quantity}</span>
              <button
                onClick={() => setQuantity(Math.min(
                  selectedVariant?.stock_quantity || 1,
                  quantity + 1
                ))}
                className="p-3 hover:bg-light transition-colors"
              >
                <Plus size={14} />
              </button>
            </div>
          </div>

          {/* Actions */}
          <div className="flex space-x-3">
            <button
              onClick={handleAddToCart}
              disabled={!selectedVariant || selectedVariant.stock_quantity === 0}
              className="btn-primary flex-1 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {selectedVariant?.stock_quantity === 0
                ? 'Out of Stock'
                : requiresMeasurements
                ? 'Provide Measurements to Add to Bag'
                : 'Add to Bag'}
            </button>
            <button
              onClick={handleWishlist}
              className="border border-light p-4 hover:border-primary transition-colors"
            >
              <Heart
                size={18}
                className={isWishlisted ? 'fill-primary text-primary' : 'text-primary'}
              />
            </button>
          </div>

          {/* Tabs */}
          <div className="border-t border-light pt-8">
            <div className="flex space-x-8 border-b border-light mb-6">
              {(['description', 'details', 'reviews'] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`pb-3 text-xs tracking-widest uppercase transition-colors ${
                    activeTab === tab
                      ? 'border-b-2 border-primary text-primary'
                      : 'text-nude hover:text-primary'
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>

            <AnimatePresence mode="wait">
              {activeTab === 'description' && (
                <motion.div
                  key="description"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="text-sm text-secondary leading-relaxed tracking-wide"
                >
                  {product.description || 'No description available.'}
                </motion.div>
              )}

              {activeTab === 'details' && (
                <motion.div
                  key="details"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="space-y-3"
                >
                  {product.material && (
                    <div className="flex justify-between text-xs">
                      <span className="tracking-widest uppercase text-nude">Material</span>
                      <span className="text-primary">{product.material}</span>
                    </div>
                  )}
                  {product.care_instructions && (
                    <div className="text-xs">
                      <span className="tracking-widest uppercase text-nude block mb-2">
                        Care Instructions
                      </span>
                      <p className="text-secondary leading-relaxed">
                        {product.care_instructions}
                      </p>
                    </div>
                  )}
                  {selectedVariant?.sku && (
                    <div className="flex justify-between text-xs">
                      <span className="tracking-widest uppercase text-nude">SKU</span>
                      <span className="text-primary">{selectedVariant.sku}</span>
                    </div>
                  )}
                </motion.div>
              )}

              {activeTab === 'reviews' && (
                <motion.div
                  key="reviews"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="space-y-6"
                >
                  {reviews.length === 0 ? (
                    <p className="text-xs tracking-widest uppercase text-nude">
                      No reviews yet
                    </p>
                  ) : (
                    reviews.map((review) => (
                      <div key={review.id} className="border-b border-light pb-6">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs tracking-wide font-medium">
                            {review.first_name} {review.last_name}
                          </span>
                          <div className="flex space-x-1">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <Star
                                key={star}
                                size={10}
                                className={
                                  star <= review.rating
                                    ? 'fill-primary text-primary'
                                    : 'text-nude'
                                }
                              />
                            ))}
                          </div>
                        </div>
                        {review.title && (
                          <p className="text-xs font-medium mb-1">{review.title}</p>
                        )}
                        {review.body && (
                          <p className="text-xs text-secondary leading-relaxed">
                            {review.body}
                          </p>
                        )}
                      </div>
                    ))
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {showMeasurementModal && (
        <MeasurementModal
          onClose={() => setShowMeasurementModal(false)}
          onSubmit={handleMeasurementSubmit}
          isSubmitting={isSubmittingMeasurements}
          categories={(product as any)?.measurement_categories}
        />
      )}

      {showSizeGuide && (
        <div
          className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
          onClick={() => setShowSizeGuide(false)}
        >
          <div
            className="bg-white w-full max-w-lg max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-light flex items-center justify-between px-6 py-4">
              <h2 className="font-serif text-xl text-primary">Size Guide</h2>
              <button
                onClick={() => setShowSizeGuide(false)}
                className="text-nude hover:text-primary transition-colors text-xl leading-none"
              >
                ×
              </button>
            </div>
            <div className="p-6">
              <p className="text-xs text-secondary leading-relaxed mb-6">
                All measurements are in inches. For the best fit, measure yourself and compare against the chart below — if you're between sizes, we recommend sizing up.
              </p>
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-light">
                    <th className="text-left py-2 tracking-widest uppercase text-nude">Size</th>
                    <th className="text-left py-2 tracking-widest uppercase text-nude">Chest</th>
                    <th className="text-left py-2 tracking-widest uppercase text-nude">Waist</th>
                    <th className="text-left py-2 tracking-widest uppercase text-nude">Hip</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { size: 'XS', chest: '32-34"', waist: '24-26"', hip: '34-36"' },
                    { size: 'S', chest: '34-36"', waist: '26-28"', hip: '36-38"' },
                    { size: 'M', chest: '36-38"', waist: '28-30"', hip: '38-40"' },
                    { size: 'L', chest: '38-40"', waist: '30-33"', hip: '40-43"' },
                    { size: 'XL', chest: '40-43"', waist: '33-36"', hip: '43-46"' },
                  ].map((row) => (
                    <tr key={row.size} className="border-b border-light">
                      <td className="py-2 font-medium">{row.size}</td>
                      <td className="py-2 text-secondary">{row.chest}</td>
                      <td className="py-2 text-secondary">{row.waist}</td>
                      <td className="py-2 text-secondary">{row.hip}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-xs text-nude leading-relaxed mt-6">
                Need a custom fit? Made-to-measure pieces let you enter your own measurements directly at checkout.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductPage;